import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  FileText, TrendingUp, LayoutDashboard, CalendarDays, BookOpenCheck, ClipboardList,
  Layers, ClipboardCheck, Navigation, Building2, Handshake, FlaskConical, Image,
  FileSignature, Wallet, Users, HardHat, Smartphone, Fingerprint, Truck,
  ScrollText, ShieldAlert, Settings, LogOut, ChevronDown, ChevronRight, Menu, X,
  ShieldCheck, Bell, KeyRound, Lock, FileCheck, MessageCircle, Mic, ShieldOff, Wifi, Trophy
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useAppSettings } from '../settings/AppSettingsContext';
import { NotificationBell } from './NotificationBell';
import { useAuthImage } from '../lib/useAuthImage';

function BrandMark({ size }) {
  const { settings, company } = useAppSettings();
  const src = useAuthImage(settings.appearance?.show_logo_in_menu ? company?.logo_url : null);
  const [tiny, setTiny] = useState(false);
  const box = size === 'lg' ? 'w-10 h-10 rounded-xl' : 'w-9 h-9 rounded-lg';
  if (src && !tiny) return <div className={`${box} bg-white flex items-center justify-center overflow-hidden flex-shrink-0 border border-slate-200 theme-fixed`} data-testid={`brand-logo-${size}`}><img src={src} alt="Logo" onLoad={e => setTiny(e.currentTarget.naturalWidth <= 1)} className="max-w-full max-h-full object-contain" /></div>;
  return <div className={`${box} ${size === 'lg' ? 'bg-emerald-600 text-white shadow-sm' : 'bg-emerald-500 text-slate-900'} flex items-center justify-center flex-shrink-0`} data-testid={`brand-icon-${size}`}><ShieldCheck className="w-5 h-5" strokeWidth={2.5} /></div>;
}
import ChangePasswordModal from './ChangePasswordModal';
import UsersManagerModal from './UsersManagerModal';
import BiometricModal from './BiometricModal';
import MobileBottomNav from './MobileBottomNav';
import GlobalSearch from './GlobalSearch';

const SECTIONS = [
  {
    label: 'DASHBOARD',
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true, badge: { text: 'KPIs', tone: 'emerald' } },
    ]
  },
  {
    label: 'COMERCIAL & VENDAS (CRM)',
    items: [
      { to: '/orcamentos', label: 'Orçamentos & Propostas', icon: FileText, badge: { text: 'NOVO', tone: 'emerald' } },
      { to: '/funil', label: 'Funil de Vendas & Follow-up', icon: TrendingUp, badge: { text: 'HOT', tone: 'orange' } },
      { to: '/whatsapp/inbox', label: 'Inbox WhatsApp (Kanban)', icon: MessageCircle, badge: { text: 'BOT', tone: 'emerald' } },
      { to: '/whatsapp/chaves', label: 'Chaves WhatsApp Meta', icon: KeyRound },
    ]
  },
  {
    label: 'OPERAÇÕES & SANITÁRIO',
    items: [
      { to: '/agenda', label: 'Agenda (Google Calendar)', icon: CalendarDays, badge: { text: 'HOJE', tone: 'amber' } },
      { to: '/pops', label: 'POPs (Procedimentos Técnicos)', icon: BookOpenCheck, badge: { text: 'NOVO', tone: 'emerald' } },
      { to: '/os', label: 'Ordens de Serviço & Laudos', icon: ClipboardList, badge: { text: 'ANVISA', tone: 'amber' } },
      { to: '/catalogo-servicos', label: 'Catálogo de Serviços & Nova O.S.', icon: Layers },
      { to: '/rotina', label: 'Rotina Diária do Operador', icon: ClipboardCheck, badge: { text: 'RDC 622', tone: 'sky' } },
      { to: '/ranking', label: 'Ranking & Premiações', icon: Trophy, badge: { text: 'NOVO', tone: 'emerald' } },
      { to: '/campo', label: 'Rotas do Técnico (Offline PWA)', icon: Truck, badge: { text: 'OFFLINE', tone: 'emerald' } },
      { to: '/telemetria', label: 'Telemetria & Rotas de Campo', icon: Navigation },
    ]
  },
  {
    label: 'CADASTROS & CLIENTES',
    items: [
      { to: '/clientes', label: 'Clientes (Comercial & Residencial)', icon: Building2 },
      { to: '/contratos', label: 'Contratos & Gráficos MRR', icon: Handshake, badge: { text: 'RECHARTS', tone: 'emerald' } },
      { to: '/produtos', label: 'Produtos Químicos & Equipamentos', icon: FlaskConical },
      { to: '/galeria', label: 'Galeria & Links de Imagens HTML', icon: Image },
    ]
  },
  {
    label: 'ADMINISTRAÇÃO & FISCAL',
    items: [
      { to: '/nfse', label: 'Enviar Nota de Serviço (NFS-e)', icon: FileSignature, badge: { text: 'NFS-e', tone: 'rose' } },
      { to: '/financeiro', label: 'Financeiro', icon: Wallet, badge: { text: 'VOZ', tone: 'emerald' } },
      { to: '/equipe', label: 'Equipe & Privilégios de Acesso', icon: Users, highlight: true },
      { to: '/tecnicos', label: 'Técnicos (Legado)', icon: HardHat },
      { to: '/certificados', label: 'Certificados & Laudos', icon: FileCheck },
      { to: '/ces', label: 'CES — Comprovante de Serviço', icon: FileCheck, badge: { text: 'INEA', tone: 'emerald' } },
      { to: '/laudos', label: 'Laudos Técnicos', icon: ScrollText },
      { to: '/raae', label: 'RAAE — Relatório de Atividades', icon: ScrollText, badge: { text: 'INEA', tone: 'emerald' } },
    ]
  },
  {
    label: 'SISTEMA',
    items: [
      { to: '/perfil-empresa', label: 'Perfil da Empresa', icon: Building2, badge: { text: 'CES', tone: 'emerald' } },
      { to: '/sst', label: 'SST & Normas Regulamentadoras', icon: ShieldAlert },
      { to: '/integracoes/agenda-boa', label: 'Integração Agenda Boa', icon: Wifi, badge: { text: 'NOVO', tone: 'emerald' } },
      { to: '/dispositivos', label: 'Meus Dispositivos', icon: Smartphone },
      { to: '/auditoria', label: 'Trilha de Auditoria', icon: FileCheck, adminOnly: true },
      { to: '/configuracoes', label: 'Configurações do App', icon: Settings, badge: { text: 'SISTEMA', tone: 'slate' } },
    ]
  },
];

const badgeTone = {
  emerald: 'bg-emerald-500 text-white',
  orange: 'bg-orange-500 text-white',
  amber: 'bg-amber-400 text-slate-900',
  sky: 'bg-sky-500 text-white',
  rose: 'bg-rose-500 text-white',
  slate: 'bg-slate-500 text-white',
};

function SidebarContent({ user, onNavigate }) {
  const { canAccess, permissions } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const initials = (user?.name || 'U').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  // Precompute visible items per section once per role change instead of on every render
  const visibleSections = useMemo(
    () => SECTIONS.map(s => ({ ...s, items: s.items.filter(it => !it.adminOnly || isAdmin) })),
    [isAdmin]
  );
  const handleBlocked = (e, label) => {
    e.preventDefault();
    e.stopPropagation();
    alert(`Acesso restrito ao módulo "${label}". Entre em contato com o administrador do sistema para solicitar a liberação.`);
  };

  return (
    <div className="theme-fixed h-full flex flex-col bg-slate-900 text-slate-100">
      {/* Header */}
      <div className="px-4 py-4 border-b border-slate-800">
        <div className="flex items-start gap-3">
          <BrandMark size="sm" />
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-bold text-white leading-tight">BioPraga OS</div>
            <div className="text-[10px] text-emerald-400 leading-tight">Padrão ANVISA & INEA-RJ</div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 px-2 py-1.5 bg-slate-800 rounded-lg">
          <div className="w-7 h-7 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-bold text-white truncate">{user?.name || 'Usuário'}</div>
            <div className="text-[9px] text-slate-400 truncate">{({ superadmin: 'Superadmin', admin: 'Administrador', tecnico: 'Técnico', vendedor: 'Vendedor', user: 'Operador' })[user?.role] || 'Usuário'}</div>
          </div>
          <Lock className="w-3.5 h-3.5 text-emerald-400" />
        </div>
      </div>

      {/* Menu */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
        {visibleSections.map(section => (
          <div key={section.label}>
            <div className="px-2 mb-1 text-[9px] font-bold tracking-wider text-emerald-400/80">
              {section.label}
            </div>
            <ul className="space-y-0.5">
              {section.items.map(item => {
                const Icon = item.icon;
                const locked = !isAdmin && !canAccess(item.to);
                if (locked) {
                  return (
                    <li key={item.to}>
                      <button
                        type="button"
                        onClick={(e) => handleBlocked(e, item.label)}
                        data-testid={`nav-locked-${item.to}`}
                        aria-disabled="true"
                        title="Acesso restrito. Entre em contato com o administrador."
                        className="w-full flex items-center justify-between gap-2 px-2 py-2 rounded-md text-[12px] text-slate-500 cursor-not-allowed opacity-60 hover:bg-slate-800/40 transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{item.label}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Lock className="w-3 h-3 text-slate-500" />
                        </div>
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.exact}
                      onClick={onNavigate}
                      className={({ isActive }) => `flex items-center justify-between gap-2 px-2 py-2 rounded-md text-[12px] transition-colors ${
                        isActive
                          ? 'bg-emerald-500/15 text-white'
                          : item.highlight
                            ? 'bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {item.badge && (
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${badgeTone[item.badge.tone] || badgeTone.emerald}`}>
                            {item.badge.text}
                          </span>
                        )}
                        <ChevronRight className="w-3 h-3 text-slate-500" />
                      </div>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="px-4 py-3 border-t border-slate-800 text-center">
        <div className="text-[10px] text-slate-500">Login & Recuperar Senha</div>
        <div className="text-[9px] text-emerald-400/60 mt-0.5">v3.2 BioPraga</div>
      </div>
    </div>
  );
}

// Mapa de rotas → títulos exibidos no cabeçalho (subtítulo abaixo de "BioPraga OS").
// Fallback: usa o label do item do menu correspondente (encontrado via SECTIONS).
const PAGE_TITLES = {
  '/':                    'Dashboard',
  '/orcamentos':          'Orçamentos & Propostas',
  '/funil':               'Funil de Vendas & Follow-up',
  '/whatsapp/inbox':      'Inbox WhatsApp (Kanban)',
  '/whatsapp/chaves':     'Chaves WhatsApp Meta',
  '/agenda':              'Agenda',
  '/pops':                'POPs (Procedimentos Técnicos)',
  '/os':                  'Ordens de Serviço & Laudos',
  '/catalogo-servicos':   'Catálogo de Serviços & Nova O.S.',
  '/rotina':              'Rotina Diária do Operador',
  '/ranking':             'Ranking & Premiações',
  '/campo':               'Rotas do Técnico',
  '/telemetria':          'Telemetria & Rotas de Campo',
  '/clientes':            'Clientes (Comercial & Residencial)',
  '/contratos':           'Contratos & Gráficos MRR',
  '/produtos':            'Produtos Químicos & Equipamentos',
  '/galeria':             'Galeria & Links de Imagens',
  '/nfse':                'Enviar Nota de Serviço (NFS-e)',
  '/financeiro':          'Financeiro',
  '/equipe':              'Equipe & Privilégios de Acesso',
  '/tecnicos':            'Técnicos',
  '/certificados':        'Certificados & Laudos',
  '/ces':                 'CES — Comprovante de Serviço',
  '/laudos':              'Laudos Técnicos',
  '/raae':                'RAAE — Relatório de Atividades',
  '/perfil-empresa':      'Perfil da Empresa',
  '/sst':                 'SST & Normas Regulamentadoras',
  '/integracoes/agenda-boa': 'Integração Agenda Boa',
  '/dispositivos':        'Meus Dispositivos',
  '/auditoria':           'Trilha de Auditoria',
  '/configuracoes':       'Configurações do App',
};
function resolvePageTitle(pathname) {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  // tenta prefixo (ex.: /os/:id)
  const match = Object.keys(PAGE_TITLES).find(p => p !== '/' && pathname.startsWith(p + '/'));
  if (match) return PAGE_TITLES[match];
  return 'Painel';
}

export default function AppLayout({ children }) {
  const { user, logout, logoutAll } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [bioOpen, setBioOpen] = useState(false);
  const [logoutAllBusy, setLogoutAllBusy] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const initials = (user?.name || 'U').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogoutAll = async () => {
    if (logoutAllBusy) return;
    const ok = window.confirm('Encerrar sessão em todos os dispositivos? Você precisará fazer login novamente em cada um deles.');
    if (!ok) return;
    setLogoutAllBusy(true);
    try {
      await logoutAll();
    } catch (e) {
      alert('Falha ao encerrar sessões. Tente novamente.');
    } finally {
      setLogoutAllBusy(false);
      setMenu(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-72 z-30">
        <SidebarContent user={user} />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-80 max-w-[90vw]">
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 z-10 p-1.5 rounded-md text-slate-300 hover:bg-slate-800">
              <X className="w-4 h-4" />
            </button>
            <SidebarContent user={user} onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <div className="lg:pl-72">
        {/* Topbar — cabeçalho padrão de todas as páginas */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200" data-testid="app-header">
          {/* Linha 1: Hambúrguer + Logo + Título/Subtítulo + Ações (Bell, Settings, Lock/Avatar) */}
          <div className="flex items-center justify-between px-4 py-3 gap-2">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <button onClick={() => setOpen(true)} aria-label="Abrir menu" data-testid="app-header-hamburger"
                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg flex-shrink-0">
                <Menu className="w-5 h-5 text-slate-700" />
              </button>
              <BrandMark size="lg" />
              <div className="leading-tight min-w-0" data-testid="app-header-title">
                <div className="text-[16px] font-bold text-slate-900 leading-tight truncate">BioPraga OS</div>
                <div className="text-[12px] text-slate-500 leading-tight truncate">{resolvePageTitle(location.pathname)}</div>
              </div>
            </div>
            <div className="hidden md:block flex-1 max-w-md mx-2">
              <GlobalSearch />
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <NotificationBell />
              {isAdmin && <button aria-label="Configurações" data-testid="app-header-settings"
                onClick={() => (window.location.href = '/configuracoes')}
                className="p-2 hover:bg-slate-100 rounded-lg">
                <Settings className="w-5 h-5 text-slate-700" />
              </button>}
              <div className="relative">
                <button data-testid="user-menu-trigger" aria-label="Menu do usuário"
                  onClick={() => setMenu(v => !v)}
                  className="p-2 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 rounded-lg transition-colors">
                  <Lock className="w-5 h-5 text-emerald-700" />
                </button>
                {menu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                      <div className="text-[13px] font-bold text-slate-900 truncate">{user?.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
                      <span className={`mt-1 inline-block text-[9px] font-bold px-1.5 py-0.5 rounded ${isAdmin ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {user?.role === 'superadmin' ? 'SUPERADMIN' : isAdmin ? 'ADMINISTRADOR' : 'USUÁRIO'}
                      </span>
                    </div>
                    <button data-testid="user-menu-change-password" onClick={() => { setMenu(false); setPwOpen(true); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 text-left"><KeyRound className="w-4 h-4" />Alterar senha</button>
                    <button data-testid="user-menu-biometric" onClick={() => { setMenu(false); setBioOpen(true); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 text-left"><Fingerprint className="w-4 h-4" />Ativar biometria</button>
                    {isAdmin && <button data-testid="user-menu-users" onClick={() => { setMenu(false); setUsersOpen(true); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 text-left"><Users className="w-4 h-4" />Gerenciar usuários</button>}
                    <div className="border-t border-slate-100" />
                    <button data-testid="user-menu-logout-all" disabled={logoutAllBusy} onClick={handleLogoutAll} className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] text-amber-700 hover:bg-amber-50 disabled:opacity-50 text-left"><ShieldOff className="w-4 h-4" />{logoutAllBusy ? 'Encerrando…' : 'Sair de todas as sessões'}</button>
                    <button data-testid="user-menu-logout" onClick={() => { setMenu(false); logout(); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] text-rose-600 hover:bg-rose-50 text-left"><LogOut className="w-4 h-4" /> Sair</button>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Linha 2: Online pill + Role (Gestor RT) */}
          <div className="flex items-center justify-between px-4 pb-3 gap-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full" data-testid="app-header-status">
              <Wifi className="w-3.5 h-3.5 text-emerald-600" />
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
              <span className="text-xs font-semibold text-emerald-700">Online (Sincronizado)</span>
            </div>
            <button data-testid="app-header-role"
              onClick={() => setMenu(v => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">
              <span className="text-xs font-semibold text-slate-700 truncate max-w-[180px]">
                {user?.role === 'superadmin' ? 'Superadmin' : isAdmin ? 'Gestor RT' : ({ tecnico: 'Técnico', vendedor: 'Vendedor' })[user?.role] || 'Operador'}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
            </button>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-6 pb-24 lg:pb-6">
          {children}
        </main>
      </div>

      <MobileBottomNav />

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} onSuccess={() => { setPwOpen(false); logout(); }} />
      <UsersManagerModal open={usersOpen} onClose={() => setUsersOpen(false)} currentUser={user} />
      <BiometricModal open={bioOpen} onClose={() => setBioOpen(false)} user={user} />
    </div>
  );
}
